// Georgetown University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Georgetown's Common Data Set 2025-2026, the PDF the Office of Assessment
//    and Decision Support links from its Common Data Set page (the link opens
//    a georgetown.box.com file, so the SOURCES url is the georgetown.edu page
//    that links it): C1 applicants, admits and enrollees for Fall 2025 with
//    the residency table (in-state means the District of Columbia); C8 test
//    policy for Fall 2027; C21/C22 plans. The CDS G5 leaves books and
//    supplies blank, so the cost comes from the financial aid office.
//  - Georgetown Undergraduate Admissions: First Year Applicants (deadlines,
//    the timeline table, Early Action rules, testing), Application
//    Requirements and Forms (recommendations, essays, interviews) and
//    Undergraduate Schools and Majors (the schools you apply to, science
//    majors, joint programs).
//  - Georgetown University news, July 30, 2026: Georgetown begins accepting
//    the Common Application for fall 2027 applicants.
//  - Office of Student Financial Aid, How Aid Works for Undergraduates:
//    2026-2027 first-year cost of attendance, living on or off campus. Its
//    tuition and fees ($74,732) equal the CDS G1 tuition ($74,520) plus
//    required fees ($212).
//  - College Scorecard, school id 131496 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Georgetown Common Data Set 2025-2026',
        url: 'https://oads.georgetown.edu/commondataset/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Georgetown Admissions, First Year Applicants',
        url: 'https://uadmissions.georgetown.edu/apply/first-year-applicants/',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Georgetown Admissions, Application Requirements and Forms',
        url: 'https://uadmissions.georgetown.edu/apply/first-year-applicants/application-requirements-and-forms/',
        read: '2026-10-01',
    },
    schools: {
        label: 'Georgetown Admissions, Undergraduate Schools and Majors',
        url: 'https://uadmissions.georgetown.edu/academic-offerings/',
        read: '2026-10-01',
    },
    commonApp: {
        label: 'Georgetown University News, Georgetown Begins Accepting the Common Application (July 30, 2026)',
        url: 'https://www.georgetown.edu/news/georgetown-begins-accepting-the-common-application-for-undergraduate-admissions/',
        read: '2026-10-01',
    },
    costs: {
        label: 'Georgetown Office of Student Financial Aid, How Aid Works for Undergraduates',
        url: 'https://finaid.georgetown.edu/undergrad/aid-for-undergrads/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?131496-Georgetown-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'georgetown-university',
    kbId: 'georgetown_university',
    name: 'Georgetown University',
    shortName: 'Georgetown',
    location: 'Washington, DC',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Georgetown visual identity color guide); decoration only.
    branding: {
        primary: '#041E42',
        secondary: '#63666A',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/georgetown_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 26822,
        admitted: 3618,
        enrolled: 1589,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 243, admitted: 48 },
            { group: 'Out-of-state', applied: 22653, admitted: 3244 },
            { group: 'International', applied: 3926, admitted: 326 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table (in-state is the District of Columbia)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Georgetown\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by undergraduate school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Georgetown has no early decision plan. Its Early Action plan is not binding; the Common Data Set marks it as restrictive and does not report how many applied or were admitted early.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to one of Georgetown\'s six undergraduate schools or two joint degree programs, which becomes your academic home. Each has its own essay prompt, and some ask for more:',
        rows: [
            { school: 'Sciences in the College of Arts & Sciences', requirement: 'Choose your science major when you apply. Admitted science students are expected to stay in that program or major for at least the first year.', source: 'schools' },
            { school: 'Public Policy, and Environment and Sustainability (joint programs)', requirement: 'Students live two years on the Hilltop Campus in Georgetown and two years on the Capitol Campus downtown, and are expected to stay in the program for at least the first year.', source: 'schools' },
            { school: 'Business and Global Affairs; International Business, Language and Culture', requirement: 'You do not apply to these joint majors as a first-year applicant: students apply to enter at the end of their first year.', source: 'schools' },
            { school: 'Recommendations by school', requirement: 'Every applicant sends a counselor letter and one teacher letter. McDonough School of Business applicants should have one from a math or social studies teacher; science, health and math applicants one from a science or math teacher; language applicants one from a world language teacher.', source: 'requirements' },
            { school: 'Essays', requirement: 'Two short essays of 250 words, a personal essay of 650 words, and a 500-word essay written for the school or program you apply to.', source: 'requirements' },
            { school: 'Alumni interview', requirement: 'Georgetown aims to interview every applicant and you must complete the interview when one is assigned. Some students abroad may not be assigned one, with no effect on their decision.', source: 'requirements' },
            { school: 'Performing arts', requirement: 'Optional: students with an exemplary talent in the performing arts they want to pursue on campus may send an additional supplement, reviewed by the Department of Performing Arts.', source: 'firstYear' },
        ],
        source: 'schools',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'By December 15', binding: false },
            { plan: 'Regular Decision', deadline: 'January 1', decision: 'By April 1', binding: false },
        ],
        note: 'The Georgetown Application opens June 1 and the Common App August 1. The application, writing supplement and supporting credentials are due by your deadline. The financial aid application is due February 1, and admitted students reply by May 1.',
        source: 'firstYear',
        binding: 'Early Action is not binding: admitted students choose freely until the May 1 decision deadline. Early Action applicants may not apply to any binding Early Decision program, but may apply to other non-binding Early Action programs.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Georgetown requires official SAT or ACT scores from every applicant: the SAT through the College Board (code 5244), the ACT through the ACT website (code 0668). It highly recommends taking the ACT science section, especially if you plan to major in the sciences, and sending any AP scores you have.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as required to be considered for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    currentChanges: [
        { topic: 'Common Application', line: 'For fall 2027 applicants, Georgetown accepts the Common Application for the first time, alongside its own Georgetown Application. Pick one; neither gives an advantage, and admissions standards stay the same.', source: 'commonApp' },
    ],

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition and fees', amount: 74732 },
            { item: 'Housing (average)', amount: 13944 },
            { item: 'Food', amount: 8236 },
            { item: 'Books and supplies', amount: 1000 },
            { item: 'Transportation', amount: 780 },
            { item: 'Personal expenses', amount: 2132 },
            { item: 'Loan fees', amount: 40 },
        ],
        total: 100864,
        note: 'For a first-year undergraduate living on or off campus. Nursing students have $75,091 in tuition and fees and $2,332 in personal expenses.',
        source: 'costs',
        netPrice: {
            average: 33661,
            byIncome: [
                { income: '$0 to $30,000', amount: 10252 },
                { income: '$30,001 to $48,000', amount: 11607 },
                { income: '$48,001 to $75,000', amount: 15746 },
                { income: '$75,001 to $110,000', amount: 21247 },
                { income: 'Over $110,000', amount: 60179 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Georgetown\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
